// Показ дат в формате ДД.ММ.ГГГГ по всему интерфейсу.
// API и поля <input type="date"> работают с ISO (2026-08-30) — это не трогаем,
// а в отрисованном тексте ISO-даты переписываются в 30.08.2026
// (с временем: 2026-08-30T10:15:00 → 30.08.2026 10:15).
//
// Время во всём сервисе — московское, независимо от часового пояса браузера.
// Бэкенд отдаёт ISO со смещением (все временные колонки — timestamptz), здесь
// это смещение переводится в МСК. Раньше оно просто отбрасывалось: регулярка
// его захватывала, а замена печатала часы как есть, то есть UTC показывался
// как московское и время уезжало на три часа назад.

export const MSK_TIME_ZONE = 'Europe/Moscow';

const DATE_OPTS = { timeZone: MSK_TIME_ZONE, day: '2-digit', month: '2-digit', year: 'numeric' };
const TIME_OPTS = { timeZone: MSK_TIME_ZONE, hour: '2-digit', minute: '2-digit' };
const DATETIME_OPTS = { ...DATE_OPTS, ...TIME_OPTS };

const toDate = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

/** Дата в МСК: 30.08.2026 */
export const fmtDate = (value, fallback = '—') => {
    const date = toDate(value);
    return date ? date.toLocaleDateString('ru-RU', DATE_OPTS) : fallback;
};

/** Дата и время в МСК: 30.08.2026, 13:15 */
export const fmtDateTime = (value, fallback = '—') => {
    const date = toDate(value);
    return date ? date.toLocaleString('ru-RU', DATETIME_OPTS) : fallback;
};

/** Только время в МСК: 13:15 */
export const fmtTime = (value, fallback = '—') => {
    const date = toDate(value);
    return date ? date.toLocaleTimeString('ru-RU', TIME_OPTS) : fallback;
};

const ISO_DATE_RE = /(^|[^\w-])(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])(?:[T ]([01]\d|2[0-3]):([0-5]\d)(?::[0-5]\d(?:\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?(?![\w-])/g;

const SKIP_TAGS = new Set(['INPUT', 'TEXTAREA', 'SCRIPT', 'STYLE', 'NOSCRIPT', 'CODE', 'PRE']);
const TEXT_ATTRS = ['title', 'aria-label', 'placeholder'];

export const isoToRu = (text) => {
    if (typeof text !== 'string' || text.length < 10 || !/\d{4}-\d{2}-\d{2}/.test(text)) return text;
    return text.replace(ISO_DATE_RE, (match, pre, y, m, d, hh, mm, offset) => {
        // Без времени — просто переставляем части: календарная дата
        // часового пояса не имеет, переводить её нельзя.
        if (!hh) return `${pre}${d}.${m}.${y}`;

        // Нет смещения — трактовать как МСК нечем, печатаем как есть.
        if (!offset) return `${pre}${d}.${m}.${y} ${hh}:${mm}`;

        const parsed = new Date(`${y}-${m}-${d}T${hh}:${mm}:00${offset === 'Z' ? 'Z' : offset}`);
        if (Number.isNaN(parsed.getTime())) return `${pre}${d}.${m}.${y} ${hh}:${mm}`;
        return `${pre}${fmtDateTime(parsed)}`;
    });
};

const isSkipped = (el) => {
    for (let node = el; node && node.nodeType === 1; node = node.parentElement) {
        if (SKIP_TAGS.has(node.tagName) || node.isContentEditable || node.hasAttribute('data-keep-iso')) return true;
    }
    return false;
};

const fixTextNode = (node) => {
    const value = node.nodeValue;
    if (!value || value.length < 10) return;
    const next = isoToRu(value);
    if (next !== value && !isSkipped(node.parentElement)) node.nodeValue = next;
};

const fixAttrs = (el) => {
    for (const attr of TEXT_ATTRS) {
        const value = el.getAttribute(attr);
        if (!value) continue;
        const next = isoToRu(value);
        if (next !== value && !isSkipped(el)) el.setAttribute(attr, next);
    }
};

const fixTree = (root) => {
    if (root.nodeType === 3) { fixTextNode(root); return; }
    if (root.nodeType !== 1 || isSkipped(root)) return;
    fixAttrs(root);
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => (
            node.nodeType === 1 && (SKIP_TAGS.has(node.tagName) || node.isContentEditable || node.hasAttribute('data-keep-iso'))
                ? NodeFilter.FILTER_REJECT
                : NodeFilter.FILTER_ACCEPT
        ),
    });
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        if (node.nodeType === 3) fixTextNode(node);
        else fixAttrs(node);
    }
};

export const startRuDateFormatting = (root = document.body) => {
    if (!root || typeof MutationObserver === 'undefined') return () => {};
    fixTree(root);
    const observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
            if (m.type === 'characterData') fixTextNode(m.target);
            else if (m.type === 'attributes') fixAttrs(m.target);
            else m.addedNodes.forEach(fixTree);
        }
    });
    observer.observe(root, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: TEXT_ATTRS,
    });
    return () => observer.disconnect();
};
